import React from 'react'
import styles from './EditGoalModal.scss'
import { ModalOverlay } from 'common/styleguide/ModalOverlay'
import { TwoCTAFooter } from 'common/styleguide/Modal'
import { CTA } from 'common/styleguide/CTA'
import { Map } from 'immutable'
import { ImageUpload } from 'common/styleguide/ImageUpload'
import { CircleSpinner } from 'common/spinners'
import { GoalTypeForm } from './GoalTypeForm'
import plusIcon from 'assets/images/goals/plusIcon.svg'
import { validateGoal } from 'lib/api/goals'
import cx from 'classnames'
import { errorMessage } from 'lib/styleguide/base.scss'
import { DatePickerInput } from 'common/styleguide/DatePickerInput'
import { goalTypeLabels, commonDateStrings } from 'lib/localization'
import { injectIntl, FormattedMessage as T, defineMessages } from 'react-intl'

const reduceBlobURL = icon => (
  typeof icon === 'string'
    ? icon
    : icon.preview
)

const componentMessages = defineMessages({
  goalNamePlaceholder: {
    id: 'EditGoalModal.goalNamePlaceholder',
    defaultMessage: 'My {goalTypeLabel} goal',
    description: 'Name placeholder for a newly created goal'
  }
})

// TODO date range validation/constraints

class _EditGoalModal extends React.PureComponent {
  constructor (props) {
    super()
    this.state = {
      pendingEdits: Map(),
      savePending: false,
      editFormSingleColumn: !(window.innerWidth >= 1000 && window.innerHeight >= 680 && window.innerHeight <= 890),
      errors: {}
    }
  }

  componentDidMount () {
    this._mediaquery = window.matchMedia('screen and (min-width: 1000px) and (min-height: 680px) and (max-height: 890px)')
    this._mediaquery.addListener(this.editFormMediaQueryHandler)
  }

  componentWillUnMount () {
    this._mediaquery.removeListener(this.editFormMediaQueryHandler)
  }

  editFormMediaQueryHandler = (e) => {
    this.setState({ editFormSingleColumn: !e.matches })
  }

  onNameChange = (e) => {
    const { pendingEdits } = this.state
    this.setState({ pendingEdits: pendingEdits.merge({ name: e.target.value }) })
  }

  onDescriptionChange = (description) => {
    const { pendingEdits } = this.state
    this.setState({ pendingEdits: pendingEdits.merge({ description }) })
  }

  onIconDrop = (files) => {
    if (!files[0]) { return }
    const icon = files[0]
    const { pendingEdits } = this.state
    this.setState({ pendingEdits: pendingEdits.merge({ icon }) })
  }

  onParamsChange = (params) => {
    const { pendingEdits } = this.state
    const goalParams = pendingEdits.get('goal_params', Map()).merge(params)
    this.setState({ pendingEdits: pendingEdits.set('goal_params', goalParams) })
  }

  onChangeGoalType = (goalType) => {
    const { pendingEdits } = this.state
    this.setState({ pendingEdits: pendingEdits.merge({ goal_type: goalType }) })
  }

  onStartDateChange = (startDate) => {
    const { pendingEdits } = this.state
    this.setState({
      pendingEdits: pendingEdits.merge({ starts_on: startDate })
    })
  }

  onEndDateChange = (endDate) => {
    const { pendingEdits } = this.state
    this.setState({
      pendingEdits: pendingEdits.merge({ ends_on: endDate })
    })
  }

  onSaveGoal = () => {
    const { goal, newGoalType, newGoal, saveGoal, intl } = this.props
    const { pendingEdits } = this.state
    const g = goal.merge(pendingEdits)
    const goalType = newGoalType || g.get('goal_type')

    const { goalValidates, errors } = validateGoal(g, goalType, newGoal, intl)

    if (goalValidates) {
      this.setState({ savePending: true, errors: {} })
      saveGoal(g).then((res) => {
        this.setState({ pendingEdits: Map() })
        this.props.onSaveComplete
          ? this.props.onSaveComplete(res)
          : this.props.onRequestClose()
      }).catch((res) => {
        this.setState({ errors: res.error, savePending: false })
      })
    } else {
      this.setState({ errors })
    }
  }

  onFormSubmit = (e) => {
    e.preventDefault()
    this.onSaveGoal()
  }

  setModalBodyRef = (ref) => {
    this._modalBodyElement = ref
    this.setState({ modalBodyElement: ref })
  }

  render () {
    const { goal, newGoal, newGoalType, metricUnits, intl } = this.props
    const { pendingEdits, modalBodyElement, editFormSingleColumn, savePending, errors } = this.state
    const g = goal.merge(pendingEdits)

    const footer = (
      <TwoCTAFooter>
        <a onClick={this.props.onRequestClose}><T id='EditGoalModal.cancelText' defaultMessage='Cancel' description='cancel form submission' /></a>
        <CTA disabled={savePending} inline margins={false} onClick={this.onSaveGoal}>
          {savePending && <CircleSpinner size={12} />} <T id='EditGoalModal.saveText' defaultMessage='Save' description='save user changes' />
        </CTA>
      </TwoCTAFooter>
    )

    const imgSrc = g.get('icon')
      ? reduceBlobURL(g.get('icon'))
      : newGoal ? plusIcon : '/images/centi_no_image.png'

    const imageHint = g.get('icon')
      ? <T id='EditGoalModal.changeImageHint' defaultMessage='Drop a photo here to change the goal image' />
      : <T id='EditGoalModal.addImageHint' defaultMessage='Drop a photo for your goal here' />

    const goalTypeLabel = intl.formatMessage(goalTypeLabels[g.get('goal_type')])

    const modalTitle = newGoal
      ? <T id='EditGoalModal.newGoalTitle' defaultMessage='New {goalTypeLabel} Goal' values={{ goalTypeLabel }} />
      : <T id='EditGoalModal.editGoalTitle' defaultMessage='Edit Goal' />

    return (
      <ModalOverlay
        shouldCloseOnOverlayClick={false}
        className={cx(styles.EditGoalModal, newGoal && styles.newGoal)}
        title={modalTitle}
        maxWidth={newGoal ? 800 : 400}
        footer={footer}
        onRequestClose={this.props.onRequestClose}
        bodyRef={this.setModalBodyRef}>
        <form onSubmit={this.onFormSubmit}>
          <div className={styles.formGroup}>
            <label htmlFor='goal-name'>
              <T id='EditGoalModal.goalName'
                defaultMessage='Name:'
                description='from field label for user-provided Goal name' />
            </label>
            {errors['name'] && <div className={errorMessage}>{errors['name']}</div>}
            <input
              id='goal-name'
              type='text'
              placeholder={intl.formatMessage(componentMessages.goalNamePlaceholder, { goalTypeLabel })}
              value={g.get('name')}
              onChange={this.onNameChange} />
          </div>

          <div className={styles.formGroup}>
            <label><T id='EditGoalModal.imageFormLabel' defaultMessage='Image:' /></label>
            <ImageUpload onDrop={this.onIconDrop} hintTop={imageHint}>
              <img className={styles.goalImg} height='80' width='80' src={imgSrc} />
            </ImageUpload>
          </div>

          {newGoal &&
            <div className={styles.formGroup}>
              <label htmlFor='startDateInput'>{intl.formatMessage(commonDateStrings.startDate)}</label>
              {errors['start_date'] && <div className={errorMessage}>{errors['start_date']}</div>}
              <DatePickerInput
                container={editFormSingleColumn && modalBodyElement}
                id='startDateInput'
                date={g.get('starts_on')}
                onDateChange={this.onStartDateChange} />
              <label htmlFor='endDateInput'>{intl.formatMessage(commonDateStrings.endDate)}</label>
              {errors['end_date'] && <div className={errorMessage}>{errors['end_date']}</div>}
              {<DatePickerInput
                container={editFormSingleColumn && modalBodyElement}
                id='endDateInput'
                date={g.get('ends_on')}
                onDateChange={this.onEndDateChange} />}
            </div>}
          {newGoal && <GoalTypeForm
            newGoalType={newGoalType}
            params={g.get('goal_params')}
            g={g}
            onParamsChange={this.onParamsChange}
            onChangeGoalType={this.onChangeGoalType}
            errors={errors}
            metricUnits={metricUnits} />}
        </form>
      </ModalOverlay>
    )
  }
}

export const EditGoalModal = injectIntl(_EditGoalModal)
